// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@product-radar/shared';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Building2, Plus, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import productRadarLogo from '@/assets/product_radar_logo_png_blue.png';

interface Workspace {
  id: string;
  name: string;
  subscription_tier: string;
  subscription_status: string;
  workspace_domain?: string;
}

interface UserCompany {
  id: string;
  name: string;
}

interface WorkspaceSelectProps {
  onSelectWorkspace: (workspaceId: string) => void;
  onSignOut: () => void;
}

function extractDomain(email: string): string {
  const part = email.split('@')[1];
  return part ? part.toLowerCase().trim() : '';
}

export function WorkspaceSelect({ onSelectWorkspace, onSignOut }: WorkspaceSelectProps) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Company selection for workspace creation
  const [userCompanies, setUserCompanies] = useState<UserCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('new');
  const [newCompanyName, setNewCompanyName] = useState('');

  useEffect(() => {
    loadUserWorkspaces();
    loadUserCompanies();
  }, []);

  const loadUserCompanies = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get companies the user has access to (via workspace membership)
      const { data: companies, error } = await supabase
        .from('companies')
        .select('id, name');

      if (error) {
        console.error('Error loading companies:', error);
        return;
      }

      setUserCompanies(companies || []);
      // Default to first existing company if available
      if (companies && companies.length > 0) {
        setSelectedCompanyId(companies[0].id);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadUserWorkspaces = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Use RPC function to get user workspaces (bypasses RLS)
      const { data: workspacesData, error: workspacesError } = await supabase
        .rpc('get_user_workspaces');

      if (workspacesError) {
        console.error('Error loading workspaces:', workspacesError);
        toast.error('Failed to load workspaces', {
          description: workspacesError.message
        });
        setIsLoading(false);
        return;
      }

      // Transform the RPC result to match the expected format
      const formattedWorkspaces = (workspacesData || []).map((ws: any) => ({
        id: ws.workspace_id,
        name: ws.workspace_name,
        subscription_tier: ws.subscription_tier,
        subscription_status: ws.subscription_status,
      }));

      setWorkspaces(formattedWorkspaces);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error loading workspaces:', error);
      toast.error('Failed to load workspaces');
      setIsLoading(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!workspaceName.trim()) {
      toast.error('Please enter a workspace name');
      return;
    }

    if (selectedCompanyId === 'new' && !newCompanyName.trim()) {
      toast.error('Please enter a company name');
      return;
    }

    setIsCreating(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !user.email) {
        toast.error('Not authenticated');
        setIsCreating(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id || user.id;

      // Extract domain from email
      const domain = extractDomain(user.email);
      const freeProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'protonmail.com', 'proton.me', 'aol.com', 'mail.com', 'live.com', 'msn.com', 'ymail.com', 'rocketmail.com', 'me.com', 'mac.com', 'zoho.com', 'gmx.com', 'fastmail.com'];
      const isPublicProvider = freeProviders.includes(domain);

      // Resolve or create company
      let companyId = selectedCompanyId;
      if (selectedCompanyId === 'new') {
        const { data: newCompany, error: companyError } = await supabase
          .from('companies')
          .insert({
            name: newCompanyName.trim(),
            created_by: userId,
          })
          .select()
          .single();

        if (companyError || !newCompany) {
          console.error('Error creating company:', companyError);
          toast.error('Failed to create company', {
            description: companyError?.message
          });
          setIsCreating(false);
          return;
        }
        companyId = newCompany.id;
      }

      // Create workspace linked to the company
      const { data: workspace, error: workspaceError } = await supabase
        .from('workspaces')
        .insert({
          name: workspaceName,
          created_by: userId,
          workspace_domain: domain,
          company_id: companyId,
        })
        .select()
        .single();

      if (workspaceError) {
        console.error('Error creating workspace:', workspaceError);
        toast.error('Failed to create workspace', {
          description: workspaceError.message
        });
        setIsCreating(false);
        return;
      }

      // Ensure membership exists
      const { error: memberError } = await supabase
        .from('workspace_members')
        .upsert(
          {
            workspace_id: workspace.id,
            user_id: user.id,
            role: 'admin',
            status: 'active',
          },
          {
            onConflict: 'workspace_id,user_id',
          }
        );

      if (memberError) {
        console.error('Error creating membership:', memberError);
        toast.error('Failed to add user to workspace', {
          description: memberError.message
        });
        setIsCreating(false);
        return;
      }

      toast.success('Workspace created successfully!');
      setShowCreateDialog(false);
      setWorkspaceName('');
      setNewCompanyName('');
      setIsCreating(false);
      
      onSelectWorkspace(workspace.id);
    } catch (error: any) {
      console.error('Error creating workspace:', error);
      toast.error('Failed to create workspace');
      setIsCreating(false);
    }
  };


  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      // The auth state change listener in App.tsx will handle navigation
      // Only call onSignOut callback after signOut succeeds
      onSignOut();
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F2F0EF]">
        <Loader2 className="h-8 w-8 animate-spin text-[#245F73]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F0EF] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8 flex flex-col items-center">
            <img src={productRadarLogo} alt="Product Radar" className="h-12 mb-4" />
            <p className="text-muted-foreground">Select a workspace to continue</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => onSelectWorkspace(workspace.id)}
                className="w-full"
              >
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-[#245F73]">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-[#245F73]/10 rounded-lg">
                      <Building2 className="h-6 w-6 text-[#245F73]" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="mb-1">{workspace.name}</h3>
                      <p className="text-sm text-muted-foreground capitalize">
                        {workspace.subscription_tier} plan
                      </p>
                    </div>
                  </div>
                </Card>
              </button>
            ))}

            <button
              onClick={() => setShowCreateDialog(true)}
              className="w-full"
            >
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-2 border-dashed hover:border-[#245F73] hover:bg-[#245F73]/5">
                <div className="flex items-center justify-center gap-3 h-full min-h-[100px]">
                  <Plus className="h-6 w-6 text-[#245F73]" />
                  <span className="text-[#245F73]">Create New Workspace</span>
                </div>
              </Card>
            </button>
          </div>

          <div className="text-center">
            <Button variant="ghost" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogDescription>
              Create a new workspace for your team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Company selection */}
            <div className="space-y-2">
              <Label htmlFor="company-select">Company</Label>
              <Select
                value={selectedCompanyId}
                onValueChange={setSelectedCompanyId}
                disabled={isCreating}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a company..." />
                </SelectTrigger>
                <SelectContent>
                  {userCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="new">+ Create new company</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* New company name (shown only when creating a new company) */}
            {selectedCompanyId === 'new' && (
              <div className="space-y-2">
                <Label htmlFor="new-company-name">Company Name</Label>
                <Input
                  id="new-company-name"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder="Acme Inc."
                  disabled={isCreating}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="e.g. Product Team"
                disabled={isCreating}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateWorkspace}
                disabled={isCreating || !workspaceName.trim() || (selectedCompanyId === 'new' && !newCompanyName.trim())}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Workspace'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
